// Tournament Import / Export (.trf / .trx)
//
// Extracted verbatim from js/app.js. Handles:
//  - Exporting the active tournament as TRF-16 / TRF-26 (per-group files) or a
//    single all-groups TRX file (uses the classic window.TRFExport / window.TRXExport
//    generators loaded via <script> tags in index.html).
//  - Importing a .trx file into tournament history (does not open it as active).
//
// initTournamentIO() wires the Import/Export buttons; call once from app.js.

import { state, saveToLocalStorage } from '../state.js';
import { putHistoryEntry } from './history-db.js';
import { escapeHtml } from '../utils/formatting.js';
import { osloDate } from '../utils/oslo-date.js';
import { sortMyTournaments, SORT_ORDERS, MY_TOURNAMENTS_SORT_STORAGE_KEY } from '../utils/tournament-sort.js';
import { nsfGroupRules } from '../utils/nsf-membership.js';

// Reads just the `order` field the user picked in the My Tournaments Sort &
// Filter panel (js/app.js), so the export picker's list uses the same
// ordering — see js/utils/tournament-sort.js for the shared key/logic. Does
// NOT read the format/type/ownership filters; the export list keeps its own
// owner-only exclusion (isTournamentShared below) instead.
function getMyTournamentsSortOrder() {
    try {
        const saved = localStorage.getItem(MY_TOURNAMENTS_SORT_STORAGE_KEY);
        if (saved && saved !== 'undefined') {
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed === 'object' && parsed.order) return parsed.order;
        }
    } catch (e) {
        console.error('Error reading My Tournaments order preference:', e);
    }
    return SORT_ORDERS.CLOSURE;
}

// --- Tournament Import Logic (.trx) ---
// ---- TRF export -----------------------------------------------------------

// A tournament shared with the current user (they're a cooperating arbiter,
// not the owner) cannot be exported — only the owner can.
function isTournamentShared(data) {
    const user = window.authApp && window.authApp.getCurrentUser && window.authApp.getCurrentUser();
    if (!user) return false;
    const emailList = (data && data.cooperatingArbiters) || [];
    return emailList.map(e => (e || '').toLowerCase()).includes((user.email || '').toLowerCase());
}

// Resolves the tournament/players/rounds/activeGroupId to export for a given
// tournament id, whether it's the currently active tournament (live state) or
// an archived one from state.history (its own frozen snapshot).
function resolveExportSource(id) {
    if (state.activeTournament && String(state.activeTournament.id) === String(id)) {
        return { t: state.activeTournament, players: state.players, rounds: state.rounds, activeGroupId: state.activeGroupId };
    }
    const entry = state.history.find(h => String(h.id) === String(id));
    if (!entry) return null;
    const data = entry.data || entry;
    return { t: data, players: entry.players || data.players || [], rounds: entry.rounds || data.rounds || [], activeGroupId: data.activeGroupId };
}

async function handleExportTournamentById(id) {
    const source = resolveExportSource(id);
    if (!source) {
        window.Dialog.error(tr('Tournament not found.'), tr('Export Error'));
        return;
    }
    if (isTournamentShared(source.t)) {
        window.Dialog.warn(tr('You cannot export a tournament that is shared with you. Only the tournament owner can export it.'), tr('Export Blocked'));
        return;
    }

    const version = await window.Dialog.prompt(
        tr('Choose the export format. TRF-16 is the classic FIDE rating format; TRF-26 is the new (2026) format; TRX is the Turneringsservice format (all groups in one file).'),
        tr('Export Tournament'),
        [
            { text: 'TRF-16', value: 'trf16', variant: 'primary' },
            { text: 'TRF-26', value: 'trf26', variant: 'primary' },
            { text: 'TRX', value: 'trx', variant: 'primary' },
            { text: tr('Cancel'), value: null }
        ]
    );
    if (!version) return;

    const profile = state.userProfile || {};
    const arbiterName = (profile.displayName ||
        `${profile.givenName || ''} ${profile.lastName || ''}`.trim()) || '';

    try {
        if (version === 'trx') {
            // TRX has no team section. The individual games in it are real and worth
            // exporting — they are what gets rated — but the file cannot say which team
            // anybody played for, so the arbiter is told before they hand it on rather
            // than discovering it at the other end. TRF-16 and TRF-26 both carry teams.
            const isTeam = (source.t.format === 'team')
                || (Array.isArray(source.t.groups) && source.t.groups.some(g => g && g.format === 'team'));
            if (isTeam) {
                const proceed = await window.Dialog.confirm(
                    tr('The Turneringsservice format has no place to record teams, so this file will contain the individual games without them. Export TRF-16 or TRF-26 instead to keep the teams.'),
                    tr('Teams Are Not Exported'),
                    tr('Export Anyway'),
                    false,
                    tr('Cancel'),
                    'warning'
                );
                if (!proceed) return;
            }
            const groups = buildExportGroups(source.t, source.players, source.rounds, source.activeGroupId);
            downloadTrxGroups(groups, source.t, arbiterName);
        } else {
            const units = buildExportUnits(source.t, source.players, source.rounds, source.activeGroupId, arbiterName);
            downloadTrfUnits(units, source.t.name, version);
        }
    } catch (err) {
        console.error('Export error:', err);
        window.Dialog.error(tr('Error exporting tournament:') + ' ' + err.message, tr('Export Error'));
    }
}

async function handleExportTournament() {
    if (!state.activeTournament) {
        window.Dialog.warn(tr('Open a tournament first, then export it as TRF.'), tr('No Active Tournament'));
        return;
    }
    return handleExportTournamentById(state.activeTournament.id);
}

function trfTypeText(format, type) {
    const fmtMap = {
        swiss: 'Swiss System',
        monrad: 'Monrad',
        rr: 'Round Robin',
        'round-robin': 'Round Robin',
        roundrobin: 'Round Robin',
        drr: 'Double Round Robin',
        'double-rr': 'Double Round Robin'
    };
    const typeMap = { standard: 'Standard', rapid: 'Rapid', blitz: 'Blitz' };
    const f = fmtMap[format] || format || 'Swiss System';
    const t = type ? (typeMap[type] || type) : '';
    return t ? `${f} (${t})` : f;
}

function trfMostCommonFed(players) {
    const counts = {};
    (players || []).forEach(p => {
        const fed = (p.fed || '').trim();
        if (fed) counts[fed] = (counts[fed] || 0) + 1;
    });
    let best = '', bestCount = 0;
    Object.keys(counts).forEach(fed => {
        if (counts[fed] > bestCount) { best = fed; bestCount = counts[fed]; }
    });
    return best;
}

function trfSanitizeFilename(name) {
    return (name || 'tournament')
        .replace(/[^a-zA-Z0-9æøåÆØÅ_-]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'tournament';
}

function trfDownload(filename, content) {
    // TRF is a fixed-width, Latin-1 (single-byte) format. Encode each char as one byte so
    // column positions are preserved even for accented names (æ, ø, å, ...).
    const bytes = new Uint8Array(content.length);
    for (let i = 0; i < content.length; i++) bytes[i] = content.charCodeAt(i) & 0xFF;
    const blob = new Blob([bytes], { type: 'text/plain;charset=ISO-8859-1' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function buildTrfUnit(t, group, groupName, arbiterName) {
    const players = group.players || [];
    const rounds = group.rounds || [];
    const pointSystem = group.pointSystem || t.pointSystem || '1-0.5-0';
    const meta = {
        name: t.name + (groupName ? ' - ' + groupName : ''),
        groupName: groupName || '',
        city: t.location || '',
        federation: t.nation || t.federation || trfMostCommonFed(players),
        startDate: (group.useGroupDates && group.startDate) ? group.startDate : t.startDate,
        endDate: t.endDate || t.startDate,
        type: trfTypeText(group.format || t.format, group.type || t.type),
        format: group.format || t.format || 'swiss',
        chiefArbiter: t.chiefArbiter || arbiterName,
        chiefArbiterFideId: t.chiefArbiterFideId || '',
        timeControl: t.timeControl || '',
        // When each round was played, for record 132. The group's own timetable when it has
        // one — groups can play on different days — otherwise the tournament's.
        roundSchedule: group.roundSchedule || t.roundSchedule || [],
        numRounds: group.roundsCount || t.rounds || rounds.length,
        pointSystem,
        seedColor: group.seedColor || t.seedColor || '',
        tiebreaks: group.tiebreaks || t.tiebreaks || [],
        // Konrad's best N of M decides the rank column, as it does the standings.
        countedRounds: (group.format || t.format) === 'konrad'
            ? ((group.konradSettings || t.konradSettings || {}).countedRounds || 0)
            : 0,
        // A TEAM tournament additionally exports records 013/310/082/352/362/320/330/300
        // (js/services/trf-team.js). Passed only when there actually are teams, so an
        // individual tournament's file is byte-for-byte what it always was.
        teams: (group.teams && group.teams.length) ? group.teams : undefined,
        teamSettings: {
            ...(group.teamSettings || t.teamSettings || {}),
            pointSystem
        }
    };
    return { meta, players, rounds };
}

function buildExportUnits(t, players, rounds, activeGroupId, arbiterName) {
    const units = [];
    if (t.groups && t.groups.length) {
        // Sync the live (active) group's players/rounds back into its group record first.
        const activeGroup = t.groups.find(g => String(g.id) === String(activeGroupId));
        if (activeGroup) {
            activeGroup.players = JSON.parse(JSON.stringify(players));
            activeGroup.rounds = JSON.parse(JSON.stringify(rounds));
        }
        // Each unit says which group it is, for the results sent to NSF (buildNsfResultFiles).
        t.groups.forEach(g => units.push(Object.assign(buildTrfUnit(t, g, g.name, arbiterName), { groupId: String(g.id) })));
    } else {
        units.push(buildTrfUnit(t, {
            players,
            rounds,
            format: t.format,
            type: t.type,
            pointSystem: t.pointSystem,
            seedColor: t.seedColor,
            roundsCount: t.rounds,
            tiebreaks: t.tiebreaks,
            teams: t.teams,
            teamSettings: t.teamSettings,
            konradSettings: t.konradSettings
        }, '', arbiterName));
    }
    return units;
}

function downloadTrfUnits(units, tournamentName, version) {
    const exportable = units.filter(u => u.players && u.players.length > 0);
    if (exportable.length === 0) {
        window.Dialog.warn(tr('This tournament has no players to export.'), tr('Nothing to Export'));
        return;
    }

    const verLabel = version === 'trf16' ? 'TRF16' : 'TRF26';
    const multi = exportable.length > 1;
    exportable.forEach(u => {
        const content = window.TRFExport.generate({
            version,
            meta: u.meta,
            players: u.players,
            rounds: u.rounds
        });
        const suffix = multi && u.meta.groupName ? '_' + trfSanitizeFilename(u.meta.groupName) : '';
        const filename = `${trfSanitizeFilename(tournamentName)}${suffix}_${verLabel}.trf`;
        trfDownload(filename, content);
    });
}

function buildExportGroups(t, players, rounds, activeGroupId) {
    let groups;
    if (t.groups && t.groups.length) {
        // Sync the live (active) group's players/rounds back into its group record first.
        const activeGroup = t.groups.find(g => String(g.id) === String(activeGroupId));
        if (activeGroup) {
            activeGroup.players = JSON.parse(JSON.stringify(players));
            activeGroup.rounds = JSON.parse(JSON.stringify(rounds));
        }
        groups = t.groups.map((g, i) => ({
            id: String(g.id),
            name: g.name || ('Group ' + (i + 1)),
            shortCode: (g.name || ('G' + (i + 1))).substring(0, 3),
            format: g.format || t.format,
            type: g.type || t.type,
            pointSystem: g.pointSystem || t.pointSystem || '1-0.5-0',
            roundsCount: g.roundsCount || t.rounds,
            tiebreaks: g.tiebreaks || t.tiebreaks || [],
            startDate: (g.useGroupDates && g.startDate) ? g.startDate : t.startDate,
            players: g.players || [],
            rounds: g.rounds || []
        }));
    } else {
        groups = [{
            name: t.name,
            shortCode: 'A',
            format: t.format,
            type: t.type,
            pointSystem: t.pointSystem || '1-0.5-0',
            roundsCount: t.rounds,
            tiebreaks: t.tiebreaks || [],
            startDate: t.startDate,
            players,
            rounds
        }];
    }

    return groups.filter(g => g.players && g.players.length > 0);
}

function downloadTrxGroups(groups, t, arbiterName) {
    if (groups.length === 0) {
        window.Dialog.warn(tr('This tournament has no players to export.'), tr('Nothing to Export'));
        return;
    }
    trfDownload(`${trfSanitizeFilename(t.name)}.trx`, trxContent(groups, t, arbiterName));
}

function trxContent(groups, t, arbiterName) {
    const user = window.authApp && window.authApp.getCurrentUser && window.authApp.getCurrentUser();
    const profile = state.userProfile || {};

    return window.TRXExport.generate({
        tournament: {
            event: t.name,
            name: t.name,
            organiser: t.organiser || t.name,
            federation: t.federation || t.nation || trfMostCommonFed(groups[0].players),
            site: t.location || '',
            startDate: t.startDate,
            endDate: t.endDate || t.startDate,
            arbiter: t.chiefArbiter || arbiterName,
            arbiterFideId: t.chiefArbiterFideId || profile.fideId || '0',
            arbiterEmail: (user && user.email) || ''
        },
        groups
    });
}

// The FIDE rating format NSF's NGP results go out in: TRF-16, the one FIDE's rating
// officers and NSF's own lists have always read. TRF-26 is a choice of the export menu.
const NSF_TRF_VERSION = 'trf16';

// A player as NSF gets them: everything the export writes except how to reach them. The
// TRX format has places for an email address and a phone number, which the arbiter's own
// export fills in; NSF's results have no use for them, and BGP's players are children.
function withoutContactDetails(players) {
    return (players || []).map(p => Object.assign({}, p, { email: '', phone: '' }));
}

/**
 * The open tournament's results as NSF asks for them (functions/lib/nsf-report.js): one
 * TRF per NGP group, and one TRX of the BGP groups — only those with players. A tournament
 * without groups is one group of its own series. The files are what the export menu would
 * download, without the players' email addresses and phone numbers. [] when there is
 * nothing to send.
 *
 * @returns {{ name: string, content: string }[]}
 */
export function buildNsfResultFiles() {
    const t = state.activeTournament;
    if (!t) return [];
    const profile = state.userProfile || {};
    const arbiterName = (profile.displayName ||
        `${profile.givenName || ''} ${profile.lastName || ''}`.trim()) || '';
    const grouped = Array.isArray(t.groups) && t.groups.length > 0;
    const seriesOf = id => nsfGroupRules(t, grouped ? id : null).series;
    const base = trfSanitizeFilename(t.name);

    const units = buildExportUnits(t, state.players, state.rounds, state.activeGroupId, arbiterName)
        .filter(u => u.players && u.players.length > 0 && seriesOf(u.groupId) === 'ngp');
    const multi = units.length > 1;
    const files = units.map(u => ({
        name: `${base}${multi && u.meta.groupName ? '_' + trfSanitizeFilename(u.meta.groupName) : ''}.trf`,
        content: window.TRFExport.generate({ version: NSF_TRF_VERSION, meta: u.meta, players: withoutContactDetails(u.players), rounds: u.rounds })
    }));

    const bgp = buildExportGroups(t, state.players, state.rounds, state.activeGroupId)
        .filter(g => g.players.length > 0 && seriesOf(g.id) === 'bgp')
        .map(g => Object.assign({}, g, { players: withoutContactDetails(g.players) }));
    if (bgp.length) files.push({ name: `${base}.trx`, content: trxContent(bgp, t, arbiterName) });
    return files;
}

/**
 * Read one imported file into a tournament record. Both supported formats arrive through
 * the same file input, and the extension is not to be trusted — a TRF is as likely to be
 * named .txt as .trf — so the format is decided by looking at the contents.
 *
 * @returns {{ tournament: Object, warnings: string[] }}
 */
function parseImportedTournament(text) {
    if (window.TRFImport && window.TRFImport.looksLikeTRF(text)) {
        return window.TRFImport.toTournament(text);
    }

    const xmlDoc = new DOMParser().parseFromString(text, 'text/xml');
    if (!xmlDoc.querySelector('Tournament')) {
        throw new Error('Not a valid TRX or TRF file');
    }
    return { tournament: parseTRX(xmlDoc), warnings: [] };
}

async function importTournamentFromFile(file, onImported) {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
        const text = e.target.result;
        try {
            const { tournament, warnings } = parseImportedTournament(text);

            if (!state.history) state.history = [];
            state.history.unshift(tournament);
            // Imported tournament is never made active, so saveToLocalStorage()'s
            // history persistence (which only writes the *active* tournament's
            // archivalData) won't cover it — persist it to IndexedDB explicitly.
            putHistoryEntry(tournament).catch(err => {
                console.error('Failed to persist imported tournament to IndexedDB:', err);
            });

            saveToLocalStorage();

            // The routine save no longer rewrites history tournaments, and an imported
            // tournament is never made active, so push its own doc to Firestore directly.
            if (window.authApp && window.authApp.isAuthenticated() && window.authApp.writeTournamentDoc) {
                const data = tournament.data || tournament;
                try {
                    await window.authApp.writeTournamentDoc(data, {
                        players: tournament.players || data.players || [],
                        rounds: tournament.rounds || data.rounds || [],
                        isLive: false,
                        // An import always mints a fresh id, so there is nothing in
                        // the cloud to check first — and on bad Wi-Fi that check is
                        // what would fail.
                        isNew: true
                    });
                } catch (err) {
                    console.error('Failed to sync imported tournament to Firestore:', err);
                }
            }

            if (typeof onImported === 'function') onImported();

            const summary = tr('Successfully imported "{name}" with {count} players.', { name: tournament.name, count: tournament.players.length });
            if (warnings && warnings.length) {
                // The tournament imported — these are things the arbiter should look at,
                // not failures. Shown rather than only logged, because every one of them
                // means a game or a score in the file needs a human decision.
                warnings.forEach(w => console.warn('Import warning:', w));
                window.Dialog.warn(
                    summary + '\n\n' + tr('Some entries in the file need checking:') + '\n' +
                        warnings.map(w => '• ' + w).join('\n'),
                    tr('Imported With Warnings')
                );
            } else {
                window.Dialog.success(summary, tr('Import Successful'));
            }
        } catch (err) {
            console.error('Import error:', err);
            // tr() on the message itself: the parsers throw their English sentence, which is
            // also its dictionary key, so this translates them without every throw site
            // needing the translator in scope.
            window.Dialog.error(tr('Error importing tournament:') + ' ' + tr(err.message), tr('Import Error'));
        }
    };
    reader.readAsText(file, 'ISO-8859-1');
}

function parseTRX(xmlDoc) {
    const tourneyNode = xmlDoc.querySelector('Tournament');
    const groupNode = xmlDoc.querySelector('Group');

    const name = tourneyNode.getAttribute('Event') || tourneyNode.getAttribute('Name') || tr('Imported Tournament');
    let startDate = tourneyNode.getAttribute('StartDate') || '';
    let endDate = tourneyNode.getAttribute('EndDate') || '';

    startDate = startDate.replace(/\./g, '-');
    endDate = endDate.replace(/\./g, '-');

    const roundsCount = groupNode ? parseInt(groupNode.getAttribute('NumRounds')) : 7;
    const activeElo = groupNode ? groupNode.getAttribute('ActiveElo') : 'standard';
    const pairingAttr = groupNode ? groupNode.getAttribute('Pairing') : '';

    let type = 'standard';
    let ratingAttr = 'FideRating';
    let kAttr = 'RatingFactor';

    if (activeElo === 'Blitz') {
        type = 'blitz';
        ratingAttr = 'FideBlitzRating';
        kAttr = 'BlitzRatingFactor';
    } else if (activeElo === 'Rapid') {
        type = 'rapid';
        ratingAttr = 'FideRapidRating';
        kAttr = 'RapidRatingFactor';
    }

    let format = 'swiss';
    if (pairingAttr === 'RoundRobin') format = 'rr';

    const players = [];
    const playerNodes = xmlDoc.querySelectorAll('Player');
    const idMap = {};

    playerNodes.forEach(node => {
        const startNo = node.getAttribute('StartNo');
        const playerId = 'p_' + Date.now() + '_' + startNo;
        idMap[startNo] = playerId;

        players.push({
            id: playerId,
            startNo: parseInt(startNo),
            givenName: node.getAttribute('Gn') || '',
            lastName: node.getAttribute('Ln') || '',
            title: node.getAttribute('Title') || '',
            club: node.getAttribute('Club') || '',
            fed: node.getAttribute('Federation') || '',
            rating: parseInt(node.getAttribute(ratingAttr)) || 0,
            standardRating: parseInt(node.getAttribute('FideRating')) || 0,
            rapidRating: parseInt(node.getAttribute('FideRapidRating')) || 0,
            blitzRating: parseInt(node.getAttribute('FideBlitzRating')) || 0,
            birthYear: node.getAttribute('BornYear') || '',
            fideId: node.getAttribute('FideId') || '',
            nsfId: node.getAttribute('LocalID') || '',
            status: node.getAttribute('Available') === 'Y',
            kFactor: node.getAttribute(kAttr) || '40'
        });
    });

    const rounds = [];
    if (groupNode) {
        const roundsMap = {};
        const processedBoards = new Set();

        const mapResult = (tsRes, isBlack) => {
            if (!tsRes || tsRes === '?') return '';
            if (tsRes === '1') return isBlack ? '0-1' : '1-0';
            if (tsRes === '0') return isBlack ? '1-0' : '0-1';
            if (tsRes === '½' || tsRes === '=') return '0.5-0.5';
            if (tsRes === '+') return isBlack ? '-/+' : '+/-';
            if (tsRes === '-') return isBlack ? '+/-' : '-/+';
            return '';
        };

        playerNodes.forEach(pNode => {
            const games = pNode.querySelectorAll('Game');
            const pId = idMap[pNode.getAttribute('StartNo')];

            games.forEach(gNode => {
                const rd = gNode.getAttribute('Rd');
                const clr = gNode.getAttribute('Clr');
                const opnt = gNode.getAttribute('Opnt');
                const res = gNode.getAttribute('Res');
                const table = parseInt(gNode.getAttribute('Table')) || 999;

                if (opnt === '0' || opnt === '-1') {
                    // BYE / WO
                    const result = mapResult(res, false);
                    if (result !== '') {
                        const boardKey = `r${rd}_${pId}_bye`;
                        if (!processedBoards.has(boardKey)) {
                            processedBoards.add(boardKey);
                            if (!roundsMap[rd]) roundsMap[rd] = [];
                            roundsMap[rd].push({ white: pId, black: 'BYE', result: result, table: 9999 });
                        }
                    }
                } else {
                    const opntId = idMap[opnt];
                    if (!opntId) return;

                    const ids = [pId, opntId].sort();
                    const boardKey = `r${rd}_${ids[0]}_${ids[1]}`;

                    if (!processedBoards.has(boardKey)) {
                        processedBoards.add(boardKey);
                        if (!roundsMap[rd]) roundsMap[rd] = [];

                        let whiteId, blackId, result;
                        if (clr === 'W') {
                            whiteId = pId;
                            blackId = opntId;
                            result = mapResult(res, false);
                        } else {
                            whiteId = opntId;
                            blackId = pId;
                            result = mapResult(res, true);
                        }
                        roundsMap[rd].push({ white: whiteId, black: blackId, result: result, table: table });
                    }
                }
            });
        });

        Object.keys(roundsMap).sort((a, b) => parseInt(a) - parseInt(b)).forEach(rd => {
            const pairings = roundsMap[rd].sort((a, b) => a.table - b.table);
            // Check if this round actually has real pairings or results
            const hasRealContent = pairings.some(p => (p.black !== 'BYE' && p.black !== 'WO') || p.result !== '');
            if (hasRealContent || format === 'rr') {
                rounds.push({ roundNo: parseInt(rd), pairings: pairings });
            }
        });
    }

    const tourneyId = Date.now().toString() + '_' + Math.floor(Math.random() * 1000);
    return {
        id: tourneyId,
        name,
        date: startDate || osloDate(),
        format: format,
        playerCount: players.length,
        roundCount: rounds.length,
        data: {
            id: tourneyId,
            name: name,
            startDate: startDate,
            endDate: endDate,
            location: tourneyNode.getAttribute('Site') || '',
            format: format,
            type: type,
            rounds: roundsCount,
            roundsCount: roundsCount,
            pointSystem: '1-0.5-0',
            date: startDate || osloDate()
        },
        players: players,
        rounds: rounds
    };
}

// Tournaments the current user is allowed to export (owner only — excludes
// anything shared with them as a cooperating arbiter), ordered the same way as
// My Tournaments per the user's persisted Order choice (active tournament
// first, same as before).
function getExportableTournamentList() {
    const list = [];
    if (state.activeTournament) {
        list.push({ id: state.activeTournament.id, name: state.activeTournament.name, date: state.activeTournament.startDate, isActive: true, data: state.activeTournament });
    }
    (state.history || []).forEach(h => {
        if (state.activeTournament && String(h.id) === String(state.activeTournament.id)) return;
        const data = h.data || h;
        list.push({ id: h.id, name: h.name, date: h.date, isActive: false, data });
    });
    return sortMyTournaments(list.filter(item => !isTournamentShared(item.data)), getMyTournamentsSortOrder());
}

function renderExportTournamentList(searchTerm) {
    const container = document.getElementById('export-tournament-list');
    if (!container) return;
    const term = (searchTerm || '').trim().toLowerCase();
    const list = getExportableTournamentList().filter(item => !term || (item.name || '').toLowerCase().includes(term));

    if (list.length === 0) {
        container.innerHTML = `<p style="text-align: center; color: var(--text-muted); padding: 1.5rem; font-style: italic;">${term ? tr('No tournaments match your search.') : tr('No tournaments available to export.')}</p>`;
        return;
    }

    container.innerHTML = list.map(item => `
        <button type="button" class="export-tournament-item" data-tournament-id="${escapeHtml(item.id)}" style="width: 100%; display: flex; flex-direction: column; align-items: flex-start; gap: 0.2rem; padding: 0.85rem 1rem; cursor: pointer; text-align: left; color: var(--text-main);">
            <span style="font-weight: 600;">${escapeHtml(item.name)}</span>
            <small style="color: var(--text-muted);">${escapeHtml(item.date || '')}${item.isActive ? ' • ' + tr('Active') : ''}</small>
        </button>
    `).join('');

    container.querySelectorAll('.export-tournament-item').forEach(btn => {
        btn.addEventListener('click', () => {
            const modal = document.getElementById('export-tournament-modal');
            if (modal) modal.classList.remove('active');
            handleExportTournamentById(btn.dataset.tournamentId);
        });
    });
}

function openExportTournamentPicker() {
    const searchInput = document.getElementById('export-tournament-search');
    if (searchInput) searchInput.value = '';
    renderExportTournamentList();
    const modal = document.getElementById('export-tournament-modal');
    if (modal) modal.classList.add('active');
    if (searchInput) searchInput.focus();
}

// Wires the Import/Export tournament buttons. `onImported` is called after a
// successful .trx import (renders the tournaments grid) since that render
// function lives in app.js.
export function initTournamentIO(onImported) {
    const btnImportTournament = document.getElementById('btn-import-tournament');
    const importTournamentInput = document.getElementById('import-tournament-input');
    const btnExportTournament = document.getElementById('btn-export-tournament');
    const btnQuickExportTournament = document.getElementById('btn-quick-export-tournament');
    const exportTournamentSearch = document.getElementById('export-tournament-search');

    if (btnImportTournament) {
        btnImportTournament.addEventListener('click', () => {
            importTournamentInput.click();
        });
    }

    if (importTournamentInput) {
        importTournamentInput.addEventListener('change', (e) => {
            importTournamentFromFile(e.target.files[0], onImported);
        });
    }

    // "My Tournaments" tab button lets the user pick which tournament to export.
    if (btnExportTournament) {
        btnExportTournament.addEventListener('click', openExportTournamentPicker);
    }

    if (exportTournamentSearch) {
        exportTournamentSearch.addEventListener('input', () => {
            renderExportTournamentList(exportTournamentSearch.value);
        });
    }

    // Dashboard quick action exports the currently open tournament directly.
    if (btnQuickExportTournament) {
        btnQuickExportTournament.addEventListener('click', handleExportTournament);
    }
}
